import { createContext, useContext, type ReactNode } from 'react';
import { useDocumentSurface } from '@/document-render/presentation/primitives/useDocumentSurface';
import { useOptionalEditableDocument } from './EditableDocumentContext';

/**
 * The selected element's own editors, drawn on the canvas instead of a side panel (LLAMS-610). The
 * editors themselves live in `blocks/presentation` - importing them here would be the forbidden
 * cross-feature direction - so the block editor supplies them through this slot, the same way it
 * supplies the prose editor (`proseEditorSlot`).
 */
export interface ElementSettingsSlot {
  /** A control drawn in place under the selected element (the Variable picker, the image dropzone). */
  renderInPlace: (fieldId: string) => ReactNode;
}

const ElementSettingsSlotContext = createContext<ElementSettingsSlot | null>(null);

export function ElementSettingsSlotProvider({ value, children }: { value: ElementSettingsSlot | null; children: ReactNode }) {
  return <ElementSettingsSlotContext.Provider value={value}>{children}</ElementSettingsSlotContext.Provider>;
}

/** Null without an editing provider, in the measurement pass, and on the export surface. */
export function useElementSettingsSlot(): ElementSettingsSlot | null {
  const editable = useOptionalEditableDocument();
  const slot = useContext(ElementSettingsSlotContext);
  const exported = useDocumentSurface() === 'export';
  if (!editable || exported || editable.structureEditable === false) return null;
  return slot;
}
